import { useCallback, useEffect, useState } from "react";
import type { JobDto, MaterialStockDto, StockLocationDto } from "@grizzly/shared";
import { api } from "../../lib/apiClient.js";
import type { ApiClientError } from "../../lib/apiClient.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";
import { AdjustStockForm } from "./adjustStockForm.js";
import { IssueStockForm } from "./issueStockForm.js";
import { StockMovementList } from "./stockMovementList.js";
import { Modal } from "../ui/modal.js";
import { TextInput } from "../ui/field.js";

export function MaterialStockPanel({
  materialId,
  locations,
  jobs,
}: {
  materialId: string;
  locations: StockLocationDto[];
  jobs: JobDto[];
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [stock, setStock] = useState<MaterialStockDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [reversing, setReversing] = useState<{ id: string } | null>(null);
  const [reverseReason, setReverseReason] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.getMaterialStock(materialId);
      setStock(res.stock);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    }
  }, [materialId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error)
    return (
      <p role="alert" className="text-sm text-action-danger">
        {error}
      </p>
    );
  if (!stock) return null;

  const reverse = async () => {
    if (!reversing) return;
    try {
      await api.reverseStockMovement(reversing.id, reverseReason);
      push(t("inventory.movementReversed"));
      setReversing(null);
      setReverseReason("");
      await load();
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    }
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">
            {stock.material.sku} · {stock.material.name}
          </h2>
          <p className="text-xs text-ink-muted">
            {t("inventory.totalOnHand")} {stock.totalOnHand}{" "}
            {stock.material.unitOfMeasure}
            {stock.lowStock ? <Badge tone="risk">{t("inventory.lowStock")}</Badge> : null}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAdjusting((v) => !v);
              setIssuing(false);
            }}
          >
            {t("inventory.adjust")}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setIssuing((v) => !v);
              setAdjusting(false);
            }}
          >
            {t("inventory.issue")}
          </Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2 text-xs text-ink-muted">
        {stock.levels.map((level) => (
          <span key={level.locationId} className="rounded border border-line px-2 py-1">
            {level.locationName}:{" "}
            <span className="font-medium text-ink">{level.onHand}</span>
          </span>
        ))}
      </div>

      {adjusting ? (
        <div className="mb-3">
          <AdjustStockForm
            materialId={materialId}
            locations={locations}
            onAdjusted={() => {
              setAdjusting(false);
              void load();
            }}
          />
        </div>
      ) : null}
      {issuing ? (
        <div className="mb-3">
          <IssueStockForm
            materialId={materialId}
            locations={locations}
            jobs={jobs}
            onIssued={() => {
              setIssuing(false);
              void load();
            }}
          />
        </div>
      ) : null}

      <h3 className="mb-2 text-sm font-semibold text-ink">
        {t("inventory.movementHistory")}
      </h3>
      {stock.movements.length === 0 ? (
        <p className="text-sm text-ink-muted">{t("inventory.noMovements")}</p>
      ) : (
        <StockMovementList
          movements={stock.movements}
          onReverse={(m) => setReversing({ id: m.id })}
        />
      )}

      <Modal
        open={reversing !== null}
        onOpenChange={(open) => {
          if (!open) setReversing(null);
        }}
        title={t("inventory.reverseTitle")}
        description={t("inventory.reverseDescription")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setReversing(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              disabled={reverseReason.trim().length < 3}
              onClick={reverse}
            >
              {t("inventory.reverseMovement")}
            </Button>
          </>
        }
      >
        <TextInput
          label={t("common.fields.reason")}
          value={reverseReason}
          onChange={(e) => setReverseReason(e.target.value)}
          hint={t("inventory.reasonHint")}
          error={null}
        />
      </Modal>
    </div>
  );
}
